import { useEffect, useState } from "react";
import { Row, Col, Image } from "react-bootstrap";
import FlexBox from "../../lib/components/common/flex-box";
import AuthenticationLayout from "../../lib/components/layouts/authentication";
import Hyperlink from "lib/components/common/hyperlink";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { ASSETS } from "assets";
import PractitionerSignupBio from "lib/components/authentication/practitioner/signup-bio";
import SimpleSlider from "lib/components/common/simple-slider";
import SimpleCard from "lib/components/common/simple-card";
import { PiCaretLeftBold } from "react-icons/pi";
import useTherapies from "lib/hooks/useTherapies";
import PractitionerSignupTherapies from "lib/components/authentication/practitioner/signup-therapies";
import PractitionerSignupDocuments from "lib/components/authentication/practitioner/signup-documents";
import SimpleStepper from "lib/components/common/stepper/simple-stepper";
import { DEFAULT_PRACTITIONER_SIGNUP_REQUEST_VALUE } from "lib/constants/default-values";
import usePractitioner from "lib/hooks/usePractitioner";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ErrorBox from "lib/components/common/error-box";

const PractitionerSignup = () => {
  const navigateTo = useNavigate();
  const [slideTo, setSlideTo] = useState<number>(0);
  const [isRenderSlideAgain, setRenderSlideAgain] = useState<boolean>(false);

  const [isUploading, setIsUploading] = useState(false);

  const [practitionerDetails, setPractitionerDetails] =
    useState<PractitionerDetails | null>(null);

  const [practitionerTherapies, setPractitionerTherapies] = useState<
    PractitionerTherapy[] | null
  >(null);

  const [practitionerDocuments, setPractitionerDocuments] =
    useState<PractitionerSignUpDocs | null>(null);

  const [signUpRequest, setSignUpRequest] =
    useState<PractitionerSignUpRequest | null>(
      DEFAULT_PRACTITIONER_SIGNUP_REQUEST_VALUE
    );

  const {
    therapies,
    getTherapies,
    loading: therapiesLoading,
    error: therapiesError,
  } = useTherapies();

  const {
    signupPractitioner,
    addPractitionerTherapies,
    getPractitionerTherapies,
    updatePractitionerProfilePic,
    getPractitionerProfilePic,
    updatePractitionerDocuments,
    getPractitionerDocuments,
    loading: practitionerTherapiesLoading,
    error: practitionerTherapiesError,
  } = usePractitioner();

  useEffect(() => {
    getTherapies();
    getPractitionerTherapies(practitionerDetails?.id, setPractitionerTherapies);

    getPractitionerProfilePic(practitionerDetails?.id, (url) =>
      setPractitionerDocuments({
        ...practitionerDocuments,
        profileImageURL: url,
      })
    );

    getPractitionerDocuments(practitionerDetails, [], (docs) =>
      setPractitionerDocuments({
        ...practitionerDocuments,
        certificateURL: docs.educationalCertificateUrl,
      })
    );
  }, []);

  const handleBioSubmission = (values: PractitionerDetails) => {
    setSignUpRequest({ ...signUpRequest, bio: values });

    signupPractitioner(values, (response) => {
      console.log("Practitioner Signup Response", response);
      setPractitionerDetails(response);
      setSlideTo(1);
    });
  };

  const handleTherapiesSubmission = (values: Therapy[]) => {
    // user-test-15175eb3-5ef7-4e06-92e7-b23c2949f5c9 <- test id
    setSignUpRequest({ ...signUpRequest, therapies: values });

    const practitionerTherapies = values.map(
      (therapy) =>
      ({
        practitionerId: practitionerDetails?.id,
        therapyId: therapy.id,
        name: therapy.name,
        perSessionPrice: "0",
        sessionTime: "0",
      } as PractitionerTherapy)
    );

    addPractitionerTherapies(
      practitionerDetails?.id,
      practitionerTherapies,
      (response) => {
        console.log("Practitioner Therapies Response", response);
        setPractitionerTherapies(response);
        setSlideTo(2);
      }
    );
  };

  const uploadProfilePic = (document: File, onSuccess: () => void) => {
    if (document) {
      updatePractitionerProfilePic(practitionerDetails?.id, document, (url) => {
        setPractitionerDocuments({
          ...practitionerDocuments,
          profileImageURL: url,
        });

        onSuccess();
      });
    } else {
      onSuccess();
    }
  };

  const uploadCertificate = (document: File, onSuccess: () => void) => {
    if (document) {
      setIsUploading(true);
      updatePractitionerDocuments(practitionerDetails?.id, document, (url) => {
        setPractitionerDocuments({
          ...practitionerDocuments,
          certificateURL: url,
        });
        setIsUploading(false);
        onSuccess();
      });
    } else {
      onSuccess();
    }
  };

  const handleDocumentsSubmission = (values: PractitionerSignUpDocs) => {
    console.log("Documents Submission", values);
    setSignUpRequest({ ...signUpRequest, documents: values });

    if (!values.profileImage && !values.profileImageURL) {
      toast.error("Please upload the profile image");
      return;
    }

    if (!values.certificate && !values.certificateURL) {
      toast.error("Please upload the certificate");
      return;
    }

    uploadProfilePic(values.profileImage, () =>
      uploadCertificate(values.certificate, () => {
        toast.success("Practitioner Sign Up Successful");
        navigateTo(PAGE_ROUTES.AUTHENTICATION.Login);
      })
    );
  };

  const handleBackButtonClick = () => {
    if (slideTo > 0) {
      setSlideTo(slideTo - 1);
    }
  };

  useEffect(() => {
    if (therapiesError || practitionerTherapiesError) {
      setRenderSlideAgain(!isRenderSlideAgain);
    }
  }, [therapiesError, practitionerTherapiesError]);

  return (
    <AuthenticationLayout>
      <Row className="g-0 min-vh-100">
        <Col lg={8} className="d-none d-lg-block">
          <Image src={ASSETS.IMAGES.LOGIN_IMAGE} alt="Sign Up" className="img-fluid vh-100" />
        </Col>

        <Col lg={4} md={12} className="d-flex justify-content-center align-items-start p-3">
          <SimpleCard className="w-100" style={{ maxHeight: '92vh', overflowY: 'auto' }}>
            <FlexBox justifyContent="start" alignItems="center" className="mb-3 px-2">
              {slideTo > 0 && (
                <h2 className="cursor-pointer me-2" onClick={handleBackButtonClick}>
                  <PiCaretLeftBold />
                </h2>
              )}
              <h2 className="mb-0">Sign Up</h2>
            </FlexBox>

            <SimpleStepper steps={["Bio", "Practice", "Documents"]} selectedStep={slideTo} />

            {(therapiesError || practitionerTherapiesError) && (
              <ErrorBox message={therapiesError || practitionerTherapiesError} />
            )}

            <SimpleSlider
              slideTo={slideTo}
              autoHeight
              className="authentication-form mt-3"
              isRenderAgain={isRenderSlideAgain}
              allowTouchMove={false}
            >
              <PractitionerSignupBio
                isLoading={therapiesLoading || practitionerTherapiesLoading}
                error={therapiesError || practitionerTherapiesError}
                onCategoryChange={() => setRenderSlideAgain(!isRenderSlideAgain)}
                onSubmit={handleBioSubmission}
              />
              <PractitionerSignupTherapies
                practitionerTherapies={practitionerTherapies ?? []}
                isLoading={therapiesLoading || practitionerTherapiesLoading}
                therapies={therapies ?? []}
                onChange={() => setRenderSlideAgain(!isRenderSlideAgain)}
                onSubmit={handleTherapiesSubmission}
              />
              <PractitionerSignupDocuments
                practitionerDocs={practitionerDocuments}
                isLoading={therapiesLoading || practitionerTherapiesLoading}
                isUploading={isUploading}
                onSubmit={handleDocumentsSubmission}
              />
            </SimpleSlider>
            <p className="text-center mt-3">
              Already Have an account?{" "}
              <Hyperlink to={PAGE_ROUTES.AUTHENTICATION.Login}>Sign In</Hyperlink>
            </p>

          </SimpleCard>
        </Col>
      </Row>
    </AuthenticationLayout>
  );
};

export default PractitionerSignup;
